import { routeById, type MiraRouteId } from '@mira/clients-shared';
import type { WebRouteState } from '../app/route-state.js';
import type { AuthStatus } from '../shell/AppShell.js';
import { Button, ButtonLink } from '../ui/primitives/index.js';
import { AuthScreen } from './auth/AuthScreens.js';
import { ChatScreen } from './chat/ChatScreen.js';
import { LegalScreen } from './legal/LegalScreens.js';
import { MemoryScreen } from './memory/MemoryScreen.js';
import { OnboardingBirthScreen, OnboardingProfileScreen, OnboardingRelationshipScreen } from './onboarding/OnboardingScreens.js';
import { RelationshipDetailScreen, RelationshipFormScreen, RelationshipsScreen } from './relationships/RelationshipScreens.js';
import { SettingsFormScreen, SettingsScreen } from './settings/SettingsScreens.js';
import { StartupScreen } from './startup/StartupScreen.js';
import { TodayScreen } from './today/TodayScreen.js';
import { TopicDetailScreen, TopicsScreen } from './topics/TopicScreens.js';

export function ScreenRouter(props: { route: WebRouteState; authStatus: AuthStatus; onRetryAuth: () => void }) {
  const route = props.route;
  if (routeById(route.routeId).requiresAuth && props.authStatus === 'guest') {
    return <AuthGateScreen returnTo={returnPath(route)} />;
  }
  if (routeById(route.routeId).requiresAuth && props.authStatus === 'checking') {
    return <StartupScreen authStatus="checking" onRetry={props.onRetryAuth} />;
  }
  if (routeById(route.routeId).requiresAuth && props.authStatus === 'failed') {
    return <StartupRetryScreen onRetry={props.onRetryAuth} />;
  }

  switch (route.routeId) {
    case 'startup':
      return <StartupScreen authStatus={props.authStatus} onRetry={props.onRetryAuth} />;
    case 'login':
      return <AuthScreen mode="login" returnTo={route.query.get('returnTo')} />;
    case 'register':
      return <AuthScreen mode="register" returnTo={route.query.get('returnTo')} />;
    case 'onboarding-profile':
      return <OnboardingProfileScreen />;
    case 'onboarding-birth':
      return <OnboardingBirthScreen showSkipSheet={route.query.get('sheet') === 'skip-time'} />;
    case 'onboarding-relationship':
      return <OnboardingRelationshipScreen />;
    case 'today':
      return <TodayScreen showRelationshipSheet={route.query.get('sheet') === 'relationship'} />;
    case 'chat':
      return (
        <ChatScreen
          autoStart={route.query.get('autostart') === '1'}
          conversationId={route.params.conversationId || 'new'}
          draft={route.query.get('draft')}
          relationshipId={route.query.get('relationshipId')}
          returnTo={route.query.get('returnTo')}
          source={route.sourceRouteId}
        />
      );
    case 'topics':
      return <TopicsScreen />;
    case 'topic-detail':
      return (
        <TopicDetailScreen
          showDelete={route.query.get('sheet') === 'delete'}
          tab={route.query.get('tab')}
          topicId={route.params.topicId}
        />
      );
    case 'relationships':
      return <RelationshipsScreen />;
    case 'relationship-new':
      return <RelationshipFormScreen initialName={route.query.get('displayName')} relationshipId={route.query.get('relationshipId')} />;
    case 'relationship-detail':
      return <RelationshipDetailScreen relationshipId={route.params.relationshipId} showDelete={route.query.get('sheet') === 'delete'} />;
    case 'memory':
      return (
        <MemoryScreen
          insightId={route.query.get('insightId')}
          memoryId={route.query.get('memoryId')}
          showInsightDelete={route.query.get('sheet') === 'delete-insight'}
          showInsightSheet={route.query.get('sheet') === 'insight'}
          showMemoryDelete={route.query.get('sheet') === 'delete-memory'}
          tab={route.query.get('tab')}
        />
      );
    case 'settings':
      return <SettingsScreen showLogout={route.query.get('sheet') === 'logout'} />;
    case 'settings-profile':
      return <SettingsFormScreen kind="profile" />;
    case 'settings-birth':
      return <SettingsFormScreen kind="birth" />;
    case 'settings-password':
      return <SettingsFormScreen kind="password" />;
    case 'settings-privacy':
      return <SettingsFormScreen kind="privacy" />;
    case 'privacy':
      return <LegalScreen kind="privacy" source={route.sourceRouteId} />;
    case 'terms':
      return <LegalScreen kind="terms" source={route.sourceRouteId} />;
    default:
      return assertNever(route.routeId);
  }
}

function StartupRetryScreen(props: { onRetry: () => void }) {
  return (
    <article className="mira-auth-screen mira-botanical-page">
      <header className="mira-auth-hero">
        <h1>Could not restore sign-in</h1>
        <p>Please retry. If your session expired, sign in again.</p>
      </header>
      <div className="mira-auth-form">
        <Button onClick={props.onRetry} type="button">Retry</Button>
        <ButtonLink href="#/auth" variant="text">Sign in</ButtonLink>
      </div>
    </article>
  );
}

function AuthGateScreen(props: { returnTo: string }) {
  const encoded = encodeURIComponent(props.returnTo);
  return (
    <article className="mira-auth-screen mira-botanical-page">
      <header className="mira-auth-hero">
        <h1>Sign in to continue</h1>
        <p>Sign in so Mira saves this action to the right account.</p>
      </header>
      <div className="mira-auth-form">
        <ButtonLink href={`#/auth?returnTo=${encoded}`} variant="primary">Sign in</ButtonLink>
        <ButtonLink href={`#/register?returnTo=${encoded}`}>Create account</ButtonLink>
        <ButtonLink href="#/auth" variant="text">Close</ButtonLink>
      </div>
    </article>
  );
}

function returnPath(route: WebRouteState): string {
  const search = route.query.toString();
  return `${route.path}${search ? `?${search}` : ''}`;
}

function assertNever(value: never): never {
  throw new Error(`Unhandled Mira route: ${String(value satisfies never)}`);
}
